import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { 
  EmailPreferences, 
  SubscriptionPreferenceForGameReminders, 
  SubscriptionPreferenceForMarketingNews 
} from '@mental-mint/shared-types';
import { getPreferences, updatePreferences } from '@mental-mint/shared-utils';
import './ManagePreferences.css';

export const ManagePreferences: React.FC = () => {
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    // Get playerId from URL query params
    const params = new URLSearchParams(window.location.search);
    const playerIdParam = params.get('playerId');

    if (!playerIdParam) {
      setError('Invalid link. Please use the link from your email.');
      setIsLoading(false);
      return;
    }

    setPlayerId(playerIdParam);
    loadPreferences(playerIdParam);
  }, []);

  const loadPreferences = async (pid: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const prefs = await getPreferences(pid);
      setPreferences(prefs);
    } catch (err: any) {
      setError(err.message || 'Failed to load preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!playerId || !preferences) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updatePreferences(playerId, {
        subscriptionPreferenceForGameReminders: preferences.subscriptionPreferenceForGameReminders,
        subscriptionPreferenceForMarketingNews: preferences.subscriptionPreferenceForMarketingNews,
      });

      setSuccessMessage('Preferences updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="preferences-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="preferences-card"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="preferences-icon-bubble"
          >
            <Lucide.Loader2 size={40} className="spinner" color="#4CAF50" strokeWidth={2} />
          </motion.div>
          <h2 className="preferences-title">Loading preferences...</h2>
          <p className="preferences-text">Please wait a moment</p>
        </motion.div>
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div className="preferences-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="preferences-card"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="preferences-icon-bubble"
            style={{ background: '#ef444420' }}
          >
            <Lucide.AlertCircle size={40} color="#ef4444" strokeWidth={2} />
          </motion.div>
          <h2 className="preferences-title" style={{ color: '#ef4444' }}>Error</h2>
          <p className="preferences-text">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="preferences-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="preferences-card"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="preferences-icon-bubble"
        >
          <Lucide.Settings size={40} color="#4CAF50" strokeWidth={2} />
        </motion.div>
        <div className="preferences-header">
          <h1>Email Preferences</h1>
          <p>Manage how we communicate with you</p>
        </div>

        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="preferences-success"
          >
            <Lucide.CheckCircle size={20} />
            <span>{successMessage}</span>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="preferences-error"
          >
            <Lucide.AlertCircle size={20} />
            <span>{error}</span>
          </motion.div>
        )}

        <div className="preferences-section">
          <div className="preference-item">
            <div className="preference-header">
              <Lucide.Mail size={24} color="#4CAF50" />
              <h3>Game Reminders</h3>
            </div>
            <p className="preference-description">
              Receive email reminders to come back and play your favorite games
            </p>
            <select
              value={preferences?.subscriptionPreferenceForGameReminders || SubscriptionPreferenceForGameReminders.NEVER}
              onChange={(e) =>
                setPreferences({
                  ...preferences!,
                  subscriptionPreferenceForGameReminders: e.target.value as SubscriptionPreferenceForGameReminders,
                })
              }
              className="preference-select"
              disabled={isSaving}
            >
              <option value={SubscriptionPreferenceForGameReminders.DAILY}>Daily</option>
              <option value={SubscriptionPreferenceForGameReminders.WEEKLY}>Weekly (on Sundays, recommended)</option>
              <option value={SubscriptionPreferenceForGameReminders.MONTHLY}>Monthly (first Sunday)</option>
              <option value={SubscriptionPreferenceForGameReminders.NEVER}>Never</option>
            </select>
          </div>

          <div className="preference-item">
            <div className="preference-header">
              <Lucide.Bell size={24} color="#4CAF50" />
              <h3>Marketing News</h3>
            </div>
            <p className="preference-description">
              Get notified when we launch new games and features
            </p>
            <select
              value={preferences?.subscriptionPreferenceForMarketingNews || SubscriptionPreferenceForMarketingNews.NEVER}
              onChange={(e) =>
                setPreferences({
                  ...preferences!,
                  subscriptionPreferenceForMarketingNews: e.target.value as SubscriptionPreferenceForMarketingNews,
                })
              }
              className="preference-select"
              disabled={isSaving}
            >
              <option value={SubscriptionPreferenceForMarketingNews.ALWAYS}>Yes, keep me updated</option>
              <option value={SubscriptionPreferenceForMarketingNews.NEVER}>No, thanks</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSave}
          className="preferences-save-button"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Lucide.Loader2 size={20} className="spinner" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Lucide.Save size={20} />
              <span>Save Preferences</span>
            </>
          )}
        </button>

        <p className="preferences-footer">
          Your privacy matters. We'll never share your email with third parties.
        </p>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spinner {
            animation: spin 1s linear infinite;
          }
        `}</style>
      </motion.div>
    </div>
  );
};
